import React, { useEffect, useState } from 'react'
import { Modal } from 'bootstrap'
import { Fields, WarningLevel } from '../../core/libraryModels'
import { KTSVG } from '../../../../../_metronic/helpers'
import type { LibraryApi } from '../../core/libraryApi'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

interface Props {
  isOpen: boolean
  onClose: () => void
  fieldData?: Fields | null
  onFieldEdited: () => void
  api: LibraryApi
}

export type { Props as EditFieldModalProps }

const EditFieldModal: React.FC<Props> = ({ onClose, isOpen, fieldData, onFieldEdited, api }) => {
  void Modal

  const [labelName, setLabelName] = useState('')
  const [inputType, setInputType] = useState('text')
  const [warningOnly, setWarningOnly] = useState<WarningLevel | null>(null)
  const [minValue, setMinValue] = useState<number | undefined>()
  const [maxValue, setMaxValue] = useState<number | undefined>()
  const [unit, setUnit] = useState('')
  const [options, setOptions] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [optionInput, setOptionInput] = useState('')
  const [isRequired, setIsRequired] = useState(false)
  const [isReadOnly, setIsReadOnly] = useState(false)
  const [editableIndex, setEditableIndex] = useState<number | null>(null)
  const [active, setActive] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    if (fieldData) {
      setLabelName(fieldData.label)
      setInputType(fieldData.fieldType)
      setMinValue(fieldData.minValue)
      setMaxValue(fieldData.maxValue)
      setUnit(fieldData.unit || '')
      setOptions(fieldData?.optionsJson ?? [])
      setWarningOnly(fieldData.warningOnly ?? null)
      setActive(fieldData.isActive)
      setIsRequired(fieldData.required)
      setIsReadOnly(fieldData.readOnly)
    }
  }, [fieldData])

  const handleEditFieldItem = async () => {
    try {
      if (!fieldData || !fieldData.id || !fieldData.submenuId) {
        console.error('Missing required data to update field')
        return
      }

      await api.updateField(
        fieldData.submenuId,
        fieldData.id,
        labelName,
        inputType,
        isRequired,
        isReadOnly,
        warningOnly ?? undefined,
        active ?? undefined,
        unit || undefined,
        minValue,
        maxValue,
        options.length > 0 ? options : undefined,
        fieldData.order
      )

      onFieldEdited()
      onClose()
    } catch (err) {
      console.error('Failed to update field:', err)
    }
  }

  const handleAddOption = () => {
    if (optionInput.trim()) {
      setOptions((prev) => [...prev, optionInput.trim()])
      setOptionInput('')
    }
  }

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)
    setMinValue(value)

    if (maxValue !== undefined && value > maxValue) {
      setError('Min value cannot be greater than max value.')
    } else {
      setError(null)
    }
  }

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)
    setMaxValue(value)

    if (minValue !== undefined && value < minValue) {
      setError('Max value cannot be less than min value.')
    } else {
      setError(null)
    }
  }

  if (!isOpen) return null

  return (
    <div className='modal-overlay'>
      <div className='modal-content' onClick={(e) => e.stopPropagation()}>
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>Add Field</h5>
          <button className='close-btn' onClick={onClose}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className='custom-modal-body'>
          <div style={{ paddingInline: '2.5rem', marginBottom: '1rem' }}>
            <div className='d-flex align-items-center mb-5'>
              <label className='modal_label me-3 mb-0'>Not editable</label>
              <input
                type='checkbox'
                className='form-check-input'
                checked={isReadOnly}
                onChange={(e) => setIsReadOnly(e.target.checked)}
              />
            </div>
            <div className='d-flex align-items-center mb-5'>
              <label className='modal_label me-3 mb-0'>Required</label>
              <input
                type='checkbox'
                className='form-check-input'
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
              />
            </div>
            <div className='d-flex align-items-center mb-5'>
              <label className='modal_label me-3 mb-0'>Active</label>
              <input
                type='checkbox'
                className='form-check-input'
                checked={Boolean(active)}
                onChange={(e) => setActive(e.target.checked)}
              />
            </div>

            <label className='modal_label'>Field Label</label>
            <input type='text' value={labelName} onChange={(e) => setLabelName(e.target.value)} className='form-control' />

            <label className='modal_label mt-5'>Choose Input Type:</label>
            <SearchableSelect
              className='form-select py-4'
              value={inputType}
              onChange={(e) => {
                setInputType(e.target.value)
                setOptions([])
              }}
            >
              <option value='text'>Text</option>
              <option value='number'>Number</option>
              <option value='date'>Date</option>
              <option value='datetime'>Date Time</option>
              <option value='checkbox'>Checkbox</option>
              <option value='radio'>Radio Buttons</option>
              <option value='select'>Dropdown (Select)</option>
              <option value='textarea'>Textarea</option>
            </SearchableSelect>

            {(inputType === 'number' || inputType === 'text') && (
              <div className='mt-4'>
                <label className='modal_label'>Validation Range</label>
                <div className='d-flex gap-3'>
                  <input
                    type='number'
                    value={minValue ?? ''}
                    onChange={handleMinChange}
                    className='form-control no-spinner'
                    placeholder='Min Value'
                  />
                  <input
                    type='number'
                    value={maxValue ?? ''}
                    onChange={handleMaxChange}
                    className='form-control no-spinner'
                    placeholder='Max Value'
                  />
                </div>
                {error && (
                  <div className='text-danger mt-2'>
                    <small>{error}</small>
                  </div>
                )}
              </div>
            )}

            {(inputType === 'select' || inputType === 'radio' || inputType === 'checkbox') && (
              <div className='mt-4'>
                <label className='modal_label'>Add Options:</label>
                <div className='d-flex gap-2'>
                  <input
                    type='text'
                    value={optionInput}
                    onChange={(e) => setOptionInput(e.target.value)}
                    className='form-control'
                    placeholder='Option value'
                  />
                  <button className='btn btn-outline-secondary' onClick={handleAddOption}>
                    Add
                  </button>
                </div>
                <ul className='mt-2 ps-0'>
                  {options.map((opt, idx) => (
                    <li
                      key={idx}
                      className='list-group-item mb-2 d-flex align-items-center justify-content-between'
                      style={{ color: '#1f2937', fontWeight: 500 }}
                    >
                      <input
                        type='text'
                        className='form-control me-2'
                        value={opt}
                        disabled={editableIndex !== idx}
                        onChange={(e) => {
                          const updatedOptions = [...options]
                          updatedOptions[idx] = e.target.value
                          setOptions(updatedOptions)
                        }}
                      />
                      {editableIndex === idx ? (
                        <button className='btn btn_success btn-sm' onClick={() => setEditableIndex(null)}>
                          Done
                        </button>
                      ) : (
                        <button className='btn btn_secondary btn-sm' onClick={() => setEditableIndex(idx)}>
                          Edit
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <label className='modal_label mt-4'>Warning Only</label>
            <SearchableSelect
              className='form-select'
              value={warningOnly ?? ''}
              onChange={(e) => setWarningOnly((e.target.value as WarningLevel) || null)}
            >
              <option value=''>None</option>
              <option value={WarningLevel.SOFT}>Soft</option>
              <option value={WarningLevel.STRICT}>Strict</option>
            </SearchableSelect>

            <label className='modal_label mt-4'>Unit</label>
            <input type='text' value={unit} onChange={(e) => setUnit(e.target.value)} className='form-control' />
          </div>
        </div>

        <div className='d-flex justify-content-end gap-2 mt-3'>
          <button className='btn btn_secondary' onClick={onClose}>
            Cancel
          </button>
          <button className='btn btn_success' onClick={handleEditFieldItem}>
            Update
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditFieldModal
